import { ApiError, type SSOKind } from '@central/api';
import { color, font } from '@central/ui';
import {
  Alert,
  Button,
  CopyButton,
  Group,
  Modal,
  PasswordInput,
  SegmentedControl,
  Switch,
  TextInput,
  Textarea,
  VisuallyHidden,
} from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { errorMessage } from '../lib/errors';
import { mono, muted } from '../lib/labels';
import { parseDomains } from '../lib/sso';

/** What the editor shows of a customer's or a staff identity provider. */
export interface EditableProvider {
  id: string;
  name: string;
  kind: SSOKind;
  enabled: boolean;
  domains: string[];
  oidc?: { issuer: string; clientId: string; clientSecretSet: boolean; redirectUri: string };
  saml?: { idpEntityId: string; spEntityId: string; acsUrl: string; metadataUrl?: string };
}

/** The create or update body both provider routes take; omitted keys are unchanged. */
export interface ProviderBody {
  kind?: SSOKind;
  name?: string;
  domains?: string[];
  enabled?: boolean;
  oidc?: { issuer?: string; clientId?: string; clientSecret?: string };
  saml?: { metadataXml?: string; metadataUrl?: string };
}

type Fields = Record<string, string>;

/** Operator text for a 400 field the server named, falling back to the server's own words. */
const placedFields = new Set([
  'name',
  'domains',
  'oidc.issuer',
  'oidc.clientId',
  'oidc.clientSecret',
  'saml.metadataXml',
  'saml.metadataUrl',
]);

const fieldText: Fields = {
  name: 'Enter a name of 1 to 120 characters.',
  'oidc.issuer': 'Enter the issuer as an https URL with no query or fragment.',
  'oidc.clientId': 'Enter the client ID the identity provider issued.',
  'oidc.clientSecret': 'Enter the client secret the identity provider issued.',
};

/**
 * Add, edit or remove one identity provider. `save` creates (no id) or
 * updates; the caller refreshes its list. With `metadataUrl`, SAML metadata
 * may also be given as a URL the server fetches once.
 */
export function SsoProviderEditor({
  provider,
  readOnly,
  metadataUrl = false,
  idpOwner,
  issuerHint = 'https://example.okta.com',
  save: saveProvider,
  remove: removeProvider,
  onSaved,
  onClose,
}: {
  /** null for a new provider. */
  provider: EditableProvider | null;
  readOnly: boolean;
  metadataUrl?: boolean;
  /** Whose identity provider the setup values go into, e.g. "the customer’s identity provider". */
  idpOwner: string;
  issuerHint?: string;
  save: (body: ProviderBody, id?: string) => Promise<EditableProvider>;
  remove: (id: string) => Promise<void>;
  onSaved: (p: EditableProvider) => void;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<SSOKind>(provider?.kind ?? 'oidc');
  const [name, setName] = useState(provider?.name ?? '');
  const [domains, setDomains] = useState(provider?.domains.join(', ') ?? '');
  const [enabled, setEnabled] = useState(provider?.enabled ?? true);
  const [issuer, setIssuer] = useState(provider?.oidc?.issuer ?? '');
  const [clientId, setClientId] = useState(provider?.oidc?.clientId ?? '');
  // Secrets and metadata start blank: blank on an existing provider keeps what is stored.
  const [clientSecret, setClientSecret] = useState('');
  const [metadataXml, setMetadataXml] = useState('');
  const [mdUrl, setMdUrl] = useState('');
  const [fields, setFields] = useState<Fields>({});
  const [confirmRemove, setConfirmRemove] = useState(false);

  const samlBody = (): ProviderBody['saml'] | undefined => {
    if (metadataUrl && mdUrl.trim()) return { metadataUrl: mdUrl.trim() };
    if (metadataXml.trim()) return { metadataXml };
    return provider ? undefined : { metadataXml };
  };
  const save = useMutation({
    mutationFn: () => {
      const ds = parseDomains(domains);
      if (!provider) {
        const body: ProviderBody =
          kind === 'oidc'
            ? {
                kind,
                name: name.trim(),
                domains: ds,
                enabled,
                oidc: { issuer: issuer.trim(), clientId: clientId.trim(), clientSecret },
              }
            : { kind, name: name.trim(), domains: ds, enabled, saml: samlBody() };
        return saveProvider(body);
      }
      const body: ProviderBody = { name: name.trim(), domains: ds, enabled };
      if (provider.kind === 'oidc') {
        body.oidc = { issuer: issuer.trim(), clientId: clientId.trim() };
        if (clientSecret) body.oidc.clientSecret = clientSecret;
      } else {
        const saml = samlBody();
        if (saml) body.saml = saml;
      }
      return saveProvider(body, provider.id);
    },
    onMutate: () => setFields({}),
    onSuccess: (p) => {
      setClientSecret('');
      setMetadataXml('');
      setMdUrl('');
      onSaved(p);
    },
    onError: (e) => {
      if (!(e instanceof ApiError)) return;
      if (e.code === 'domain_taken') setFields({ domains: e.message });
      else if (e.fields) {
        const f: Fields = {};
        for (const [k, v] of Object.entries(e.fields)) f[k] = fieldText[k] ?? v;
        setFields(f);
      }
    },
  });
  const remove = useMutation({
    mutationFn: () => removeProvider(provider!.id),
    onSuccess: () => onClose(),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  // Every error the server placed on a field this form shows; anything else is an alert.
  const fieldsShown =
    Object.keys(fields).length > 0 && Object.keys(fields).every((k) => placedFields.has(k));
  const title = provider
    ? readOnly
      ? provider.name
      : `Edit ${provider.name}`
    : 'Add identity provider';

  return (
    <Modal opened onClose={onClose} title={title} size="lg">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        {!provider && (
          <SegmentedControl
            aria-label="Protocol"
            value={kind}
            onChange={(v) => setKind(v as SSOKind)}
            data={[
              { value: 'oidc', label: 'OpenID Connect' },
              { value: 'saml', label: 'SAML' },
            ]}
          />
        )}
        <TextInput
          label="Name"
          description="Shown to users on sign-in, e.g. Okta or Entra ID."
          data-autofocus
          disabled={readOnly}
          value={name}
          error={fields.name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <TextInput
          label="Email domains"
          description="Comma-separated. Only these domains may sign in through this provider."
          disabled={readOnly}
          value={domains}
          error={fields.domains}
          onChange={(e) => setDomains(e.currentTarget.value)}
        />
        <Switch
          label="Enabled"
          disabled={readOnly}
          checked={enabled}
          onChange={(e) => setEnabled(e.currentTarget.checked)}
        />
        {kind === 'oidc' ? (
          <>
            <TextInput
              label="Issuer"
              placeholder={issuerHint}
              disabled={readOnly}
              value={issuer}
              error={fields['oidc.issuer']}
              onChange={(e) => setIssuer(e.currentTarget.value)}
            />
            <TextInput
              label="Client ID"
              disabled={readOnly}
              value={clientId}
              error={fields['oidc.clientId']}
              onChange={(e) => setClientId(e.currentTarget.value)}
            />
            {!readOnly && (
              <PasswordInput
                label="Client secret"
                autoComplete="off"
                description={
                  provider?.oidc?.clientSecretSet
                    ? 'A secret is stored and never shown. Leave blank to keep it.'
                    : undefined
                }
                value={clientSecret}
                error={fields['oidc.clientSecret']}
                onChange={(e) => setClientSecret(e.currentTarget.value)}
              />
            )}
          </>
        ) : (
          !readOnly && (
            <>
              {metadataUrl && (
                <TextInput
                  label="IdP metadata URL"
                  description={
                    provider?.saml?.metadataUrl
                      ? `Fetched from ${provider.saml.metadataUrl}. Enter a URL to fetch it again, or paste XML below.`
                      : 'Central fetches the federation metadata once, when you save. Or paste the XML below.'
                  }
                  placeholder="https://login.microsoftonline.com/…/federationmetadata.xml"
                  value={mdUrl}
                  error={fields['saml.metadataUrl']}
                  onChange={(e) => setMdUrl(e.currentTarget.value)}
                />
              )}
              <Textarea
                label="IdP metadata XML"
                description={
                  provider
                    ? `Current IdP: ${provider.saml?.idpEntityId ?? '–'}. Leave blank to keep the stored metadata.`
                    : 'Paste the identity provider’s federation metadata.'
                }
                autosize
                minRows={4}
                maxRows={12}
                styles={{ input: { fontFamily: font.mono, fontSize: 12 } }}
                value={metadataXml}
                error={fields['saml.metadataXml']}
                onChange={(e) => setMetadataXml(e.currentTarget.value)}
              />
            </>
          )
        )}
        {provider && <SetupValues p={provider} idpOwner={idpOwner} />}
        {save.error && !fieldsShown && <Alert color="red">{errorMessage(save.error)}</Alert>}
        {remove.error && <Alert color="red">{errorMessage(remove.error)}</Alert>}
        {!readOnly &&
          (confirmRemove ? (
            <Alert color="red" title={`Remove ${provider!.name}?`}>
              <p style={{ margin: '0 0 10px' }}>
                Every user’s link to it is removed. They sign in with their password until single
                sign-on is set up again.
              </p>
              <Group>
                <Button
                  color="red"
                  size="xs"
                  loading={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  Remove provider
                </Button>
                <Button variant="default" size="xs" onClick={() => setConfirmRemove(false)}>
                  Keep it
                </Button>
              </Group>
            </Alert>
          ) : (
            <Group justify="space-between">
              {provider ? (
                <Button variant="subtle" color="red" onClick={() => setConfirmRemove(true)}>
                  Remove…
                </Button>
              ) : (
                <span />
              )}
              <Group>
                <Button variant="default" onClick={onClose}>
                  {provider ? 'Close' : 'Cancel'}
                </Button>
                <Button type="submit" color="dark" loading={save.isPending}>
                  {provider ? 'Save' : 'Add provider'}
                </Button>
              </Group>
            </Group>
          ))}
      </form>
    </Modal>
  );
}

/** The values the identity provider is configured with, each with a copy button. */
function SetupValues({ p, idpOwner }: { p: EditableProvider; idpOwner: string }) {
  const rows: Array<[string, string]> = p.oidc
    ? [['Redirect URI', p.oidc.redirectUri]]
    : p.saml
      ? [
          ['SP entity ID and metadata URL', p.saml.spEntityId],
          ['ACS URL', p.saml.acsUrl],
        ]
      : [];
  if (rows.length === 0) return null;
  return (
    <section
      aria-label="Identity provider setup"
      style={{ background: color.page, borderRadius: 8, padding: '12px 14px' }}
    >
      <p style={{ margin: '0 0 8px', fontSize: 13, ...muted }}>Enter these in {idpOwner}.</p>
      {rows.map(([label, value]) => (
        <div
          key={label}
          style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '6px 0' }}
        >
          <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
            <span style={{ ...mono, wordBreak: 'break-all', flex: 1 }}>{value}</span>
            <CopyButton value={value}>
              {({ copied, copy }) => (
                <Button variant="default" size="xs" onClick={copy}>
                  {copied ? 'Copied' : 'Copy'} <VisuallyHidden>{label}</VisuallyHidden>
                </Button>
              )}
            </CopyButton>
          </div>
        </div>
      ))}
    </section>
  );
}
